import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/solid';
import { trackContact } from './GoogleAnalytics';
import { usePublicFloatingCtaVisible } from '../hooks/usePublicFloatingCtaVisible';

/**
 * Primary floating CTA: opens /contact (site contact form).
 * Hidden while the cookie notice is up (it would steal taps on phones)
 * and on /contact so it cannot cover Send Message.
 *
 * Animate the inner chip, not the fixed wrapper — iOS mis-hits
 * position:fixed elements that also have a CSS transform.
 */
export default function FloatingContactButton() {
  const visible = usePublicFloatingCtaVisible();
  const reduceMotion = useReducedMotion();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const handleClick = () => {
    trackContact('form');
  };

  if (!visible) return null;

  return (
    <div
      className="fixed z-50 right-[calc(1.5rem+env(safe-area-inset-right,0px))] bottom-[calc(1.5rem+env(safe-area-inset-bottom,0px))]"
    >
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 16, scale: 0.94 }}
        animate={mounted && !reduceMotion ? { opacity: 1, y: 0, scale: 1 } : mounted ? { opacity: 1 } : {}}
        transition={{ type: 'spring', stiffness: 280, damping: 24, delay: 0.35 }}
      >
        <motion.div whileHover={reduceMotion ? undefined : { y: -2 }} whileTap={reduceMotion ? undefined : { scale: 0.98 }}>
          <Link
            to="/contact"
            onClick={handleClick}
            aria-label="Send us a message — opens the contact form"
            className="group relative flex items-center gap-2 overflow-hidden rounded-full bg-gradient-to-br from-primary-500 via-primary-600 to-primary-800 px-4 py-3 text-white shadow-lg outline-none ring-offset-2 transition-shadow hover:shadow-2xl focus-visible:ring-4 focus-visible:ring-primary-400 sm:gap-3 sm:px-5 sm:py-3.5"
          >
            {!reduceMotion && (
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-full bg-primary-400 opacity-25 animate-ping [animation-duration:2.8s]"
              />
            )}
            {/* Sheen on hover — subtle premium feel */}
            <span
              aria-hidden
              className="pointer-events-none absolute -left-full top-0 h-full w-1/2 skew-x-12 bg-white/15 opacity-0 transition-all duration-500 group-hover:left-full group-hover:opacity-100"
            />
            <ChatBubbleLeftRightIcon className="relative z-10 h-6 w-6 shrink-0 sm:h-7 sm:w-7" aria-hidden />
            <span className="relative z-10 text-sm font-semibold tracking-tight sm:text-base">
              <span className="sm:hidden">Message us</span>
              <span className="hidden sm:inline">Send a message</span>
            </span>
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
}
